import React, { useEffect, useState, useMemo } from "react";
import { Plus, Trash2, Edit2, Download, FileText, Mail, CheckCircle2, Undo2, Wallet, ArrowRight, CheckCircle, Send, CopyX, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { PaidCounter } from "./PaidCounter";
import SmartReconcileModal from "./SmartReconcileModal";

export default function AllotmentTab({ event }) {
  const [committees, setCommittees] = useState([]);
  const [allotments, setAllotments] = useState([]);
  const [selCom, setSelCom] = useState("");
  const [form, setForm] = useState({ committee_id: "", student_name: "", student_email: "", student_phone: "", portfolio: "", school_name: "", paid: false });
  const [editing, setEditing] = useState(null);
  const [cashPending, setCashPending] = useState([]);
  const [linkingReceipt, setLinkingReceipt] = useState(null); // receipt_id that will be linked on submit
  const [saving, setSaving] = useState(false);
  const [dupModal, setDupModal] = useState(false);
  const [cashDupModal, setCashDupModal] = useState(false);
  const [schoolPdfModal, setSchoolPdfModal] = useState(false);
  const [smartModal, setSmartModal] = useState(false);

  const load = async () => {
    const [c, a, r] = await Promise.all([
      api.get(`/events/${event.event_id}/committees`),
      api.get(`/events/${event.event_id}/allotments`),
      api.get(`/accountant/allotments/unassigned?event_id=${event.event_id}`).catch(() => ({ data: [] })),
    ]);
    setCommittees(c.data); setAllotments(a.data); setCashPending(r.data || []);
    if (!selCom && c.data[0]) { setSelCom(c.data[0].committee_id); setForm((f) => ({ ...f, committee_id: c.data[0].committee_id })); }
  };
  useEffect(() => { load(); }, [event.event_id]);

  const allottedByCom = useMemo(() => {
    const map = {};
    allotments.forEach((a) => { (map[a.committee_id] = map[a.committee_id] || new Set()).add(a.portfolio); });
    return map;
  }, [allotments]);

  const currentCom = committees.find((c) => c.committee_id === form.committee_id);
  const availablePortfolios = currentCom ? (currentCom.matrix || []).filter((p) => !((allottedByCom[currentCom.committee_id] || new Set()).has(p)) || (editing && editing.portfolio === p)) : [];
  const lockedPortfolios = currentCom ? (currentCom.matrix || []).filter((p) => (allottedByCom[currentCom.committee_id] || new Set()).has(p) && !(editing && editing.portfolio === p)) : [];

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return; // double-click guard — the unique (committee, portfolio) index is the server-side backstop
    if (!form.committee_id || !form.portfolio) return toast.error("Pick a committee and portfolio");
    setSaving(true);
    try {
      let createdAllotmentId = null;
      if (editing) {
        await api.patch(`/allotments/${editing.allotment_id}`, form);
        toast.success("Updated");
      } else {
        const resp = await api.post(`/events/${event.event_id}/allotments`, form);
        createdAllotmentId = resp?.data?.allotment_id || null;
        toast.success("Allotment created. PDF email queued.");
      }
      // If this submission was linked to a cash receipt, mark the receipt so
      // the delegate disappears from the "Cash paid — not yet allotted" list.
      if (linkingReceipt && createdAllotmentId) {
        try {
          await api.post(`/accountant/receipts/${linkingReceipt}/link-allotment`, { allotment_id: createdAllotmentId });
        } catch { /* non-fatal */ }
      }
      setForm({ committee_id: form.committee_id, student_name: "", student_email: "", student_phone: "", portfolio: "", school_name: "", paid: false });
      setEditing(null); setLinkingReceipt(null);
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const startFromCashReceipt = (r) => {
    setEditing(null);
    setLinkingReceipt(r.receipt_id);
    setForm({
      committee_id: form.committee_id || (committees[0]?.committee_id ?? ""),
      student_name: r.payer_name || "",
      student_email: r.payer_email || "",
      student_phone: r.payer_phone || "",
      portfolio: "",
      school_name: "",
      paid: true,
    });
    // scroll form into view on mobile
    setTimeout(() => document.querySelector('[data-testid="allot-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    toast.info(`Prefilled from receipt ${r.receipt_number}. Pick committee + portfolio, then Allot.`);
  };

  const startEdit = (a) => {
    setEditing(a);
    setForm({ committee_id: a.committee_id, student_name: a.student_name, student_email: a.student_email, student_phone: a.student_phone, portfolio: a.portfolio, school_name: a.school_name, paid: a.paid });
    setSelCom(a.committee_id);
  };

  const del = async (a) => {
    if (!window.confirm("Release this allotment?")) return;
    await api.delete(`/allotments/${a.allotment_id}`);
    load();
  };

  const reverseCheckin = async (a) => {
    if (!window.confirm(`Undo check-in for ${a.student_name}? This will mark the delegate as not checked in.`)) return;
    try {
      await api.post(`/allotments/${a.allotment_id}/reverse-checkin`, {});
      toast.success("Check-in reversed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reverse check-in");
    }
  };

  const downloadAllot = async (a) => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/allotments/${a.allotment_id}/pdf?token=${token}`, "_blank");
  };

  const resendAllot = async (a) => {
    const defaultTo = a.student_email || "";
    const to = window.prompt(
      `Resend the allotment email to which address?\n\nThe latest PDF (with the current committee, portfolio & payment status) will be regenerated and mailed.`,
      defaultTo,
    );
    if (to === null) return; // cancelled
    const clean = (to || "").trim();
    if (clean && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) { toast.error("Invalid email address"); return; }
    try {
      const res = await api.post(`/allotments/${a.allotment_id}/resend`,
        clean && clean !== defaultTo ? { recipient: clean } : {});
      toast.success(`Allotment resent to ${res.data.sent_to}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const downloadAttendance = () => {
    const token = localStorage.getItem("munners_token");
    // Per-committee PDF for the currently selected committee (easy to share).
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/attendance.pdf?committee_id=${selCom}&t=${token}`, "_blank");
  };
  const downloadAttendanceAll = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/attendance-bundle.zip?t=${token}`, "_blank");
  };
  const downloadMarking = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/marking.pdf?committee_id=${selCom}&t=${token}`, "_blank");
  };
  const downloadMarkingAll = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/marking-bundle.zip?t=${token}`, "_blank");
  };
  const downloadAllAllotments = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/allotments.xlsx?t=${token}`, "_blank");
  };

  const [paidFilter, setPaidFilter] = useState("all"); // "all" | "paid" | "unpaid"
  const comAllAllotments = allotments.filter((a) => a.committee_id === selCom);
  const comPaid = comAllAllotments.filter((a) => a.paid).length;
  const comAllotments = comAllAllotments.filter((a) => paidFilter === "all" || (paidFilter === "paid") === !!a.paid);
  const comCheckedIn = comAllotments.filter((a) => a.checked_in);
  const comNotCheckedIn = comAllotments.filter((a) => !a.checked_in);

  const renderRow = (a) => (
    <tr key={a.allotment_id} className="border-b border-slate-50" data-testid={`allotment-${a.allotment_id}`}>
      <td className="py-2 pr-2 font-semibold text-slate-900">{a.portfolio}</td>
      <td className="py-2 pr-2 text-slate-700">{a.student_name}<div className="text-xs text-slate-400">{a.student_email}</div></td>
      <td className="py-2 pr-2 text-slate-600 text-xs">{a.school_name}</td>
      <td className="py-2 pr-2">{a.paid ? <span className="role-pill bg-green-100 text-green-700">Paid</span> : <span className="role-pill bg-amber-100 text-amber-700">Pending</span>}</td>
      <td className="py-2 pr-2">
        {a.checked_in
          ? <span className="role-pill bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {a.checked_in_at ? new Date(a.checked_in_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Checked in"}</span>
          : <span className="role-pill bg-slate-100 text-slate-500">Not yet</span>}
      </td>
      <td className="py-2 pr-2 flex gap-1 justify-end">
        {a.checked_in && (
          <button onClick={() => reverseCheckin(a)} className="p-1.5 rounded hover:bg-amber-50" title="Undo check-in" data-testid={`reverse-checkin-${a.allotment_id}`}><Undo2 className="w-4 h-4 text-amber-600" /></button>
        )}
        <button onClick={() => downloadAllot(a)} className="p-1.5 rounded hover:bg-slate-100" title="PDF"><FileText className="w-4 h-4 text-slate-500" /></button>
        <button
          onClick={() => resendAllot(a)}
          className="p-1.5 rounded hover:bg-sky-50"
          title={a.last_resent_at ? `Resent to ${a.last_resent_to || a.student_email} on ${(a.last_resent_at || "").slice(0, 16).replace("T", " ")}` : "Resend the allotment email to the student with the latest PDF"}
          data-testid={`resend-allot-${a.allotment_id}`}
        ><Send className="w-4 h-4 text-sky-600" /></button>
        <button onClick={() => startEdit(a)} className="p-1.5 rounded hover:bg-slate-100"><Edit2 className="w-4 h-4 text-slate-500" /></button>
        <button onClick={() => del(a)} className="p-1.5 rounded hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-500" /></button>
      </td>
    </tr>
  );

  return (
    <div>
      <PaidCounter eventId={event.event_id} refreshKey={allotments} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display font-bold text-xl">Allotment Portal</h3>
          <p className="text-sm text-slate-500">Add committees first. Locked portfolios are already allotted.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setSmartModal(true)} className="btn-primary" data-testid="smart-reconcile-btn" title="Find people who paid but aren't allotted, allotments with no payment, and names written differently"><Sparkles className="w-4 h-4" /> Smart reconcile</button>
          <button onClick={() => setDupModal(true)} className="btn-ghost" data-testid="find-duplicate-allotments-btn" title="List allotments sharing the same committee+portfolio, student name, or email — delete extras in place"><CopyX className="w-4 h-4" /> Find duplicates</button>
          <button onClick={() => setCashDupModal(true)} className="btn-ghost" data-testid="find-duplicate-cash-btn" title="List receipts (cash AND online, allotted or not) sharing the same receipt number or payer + amount — delete extras in place"><CopyX className="w-4 h-4" /> Find receipt duplicates</button>
          <button onClick={() => setSchoolPdfModal(true)} className="btn-ghost" data-testid="download-school-letters-btn" title="Download the emailed allotment letters for every student of the selected school(s), one student per page"><FileText className="w-4 h-4" /> Letters by school (PDF)</button>
          <button onClick={downloadAllAllotments} className="btn-ghost" data-testid="download-allotments-xlsx-btn"><Download className="w-4 h-4" /> Allotments (Excel)</button>
          <button onClick={downloadAttendance} className="btn-ghost" data-testid="download-attendance-btn" title="PDF for the currently selected committee"><Download className="w-4 h-4" /> Attendance (this committee)</button>
          <button onClick={downloadAttendanceAll} className="btn-ghost" data-testid="download-attendance-zip-btn" title="ZIP with a separate attendance PDF per committee"><Download className="w-4 h-4" /> Attendance (all committees)</button>
          <button onClick={downloadMarking} className="btn-ghost" data-testid="download-marking-btn" title="PDF for the currently selected committee"><Download className="w-4 h-4" /> Marking (this committee)</button>
          <button onClick={downloadMarkingAll} className="btn-ghost" data-testid="download-marking-zip-btn" title="ZIP with a separate marking PDF per committee"><Download className="w-4 h-4" /> Marking (all committees)</button>
        </div>
      </div>

      {cashPending.length > 0 && (
        <div className="dashboard-card p-5 mb-5 border-l-4 border-orange-400 bg-orange-50/40" data-testid="cash-unallotted-panel">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center"><Wallet className="w-4 h-4" /></div>
              <div>
                <div className="font-display font-black text-base">Cash paid — not yet allotted</div>
                <div className="text-xs text-slate-500">{cashPending.length} delegate{cashPending.length === 1 ? "" : "s"} with an accounts-issued receipt but no committee/portfolio yet. Click <b>Allot</b> to prefill the form.</div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b border-orange-200">
                <th className="py-2 pr-2">Receipt #</th>
                <th className="py-2 pr-2">Payer</th>
                <th className="py-2 pr-2">Contact</th>
                <th className="py-2 pr-2 text-right">Amount</th>
                <th className="py-2 pr-2">Issued</th>
                <th className="py-2 pr-2 text-right">Action</th>
              </tr></thead>
              <tbody>
                {cashPending.map((r) => (
                  <tr key={r.receipt_id} className="border-b border-orange-100" data-testid={`cash-receipt-${r.receipt_id}`}>
                    <td className="py-2 pr-2 font-mono font-bold">{r.receipt_number}</td>
                    <td className="py-2 pr-2">{r.payer_name}</td>
                    <td className="py-2 pr-2 text-xs text-slate-500">{r.payer_email || r.payer_phone || "—"}</td>
                    <td className="py-2 pr-2 text-right font-bold">₹{(r.amount_paise / 100).toLocaleString("en-IN")}</td>
                    <td className="py-2 pr-2 text-xs text-slate-500">{(r.issued_at || "").slice(0, 10)}</td>
                    <td className="py-2 pr-2 text-right">
                      {committees.length > 0 ? (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => startFromCashReceipt(r)} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-orange-500 text-white hover:bg-orange-600" data-testid={`allot-from-cash-${r.receipt_id}`}>
                            Allot <ArrowRight className="w-3 h-3" />
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Mark ${r.payer_name} as allotted WITHOUT assigning committee/portfolio?\n\n(They'll disappear from this cash list. Use "Allot" instead if you want to assign a committee now.)`)) return;
                              try {
                                await api.patch(`/accountant/receipts/${r.receipt_id}`, { allotted: true });
                                toast.success(`${r.payer_name} marked allotted`);
                                load();
                              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-white border border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                            title="Mark allotted without assigning a committee"
                            data-testid={`mark-allotted-${r.receipt_id}`}
                          >
                            <CheckCircle className="w-3 h-3" /> Mark allotted
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Mark ${r.payer_name} as allotted?`)) return;
                            try {
                              await api.patch(`/accountant/receipts/${r.receipt_id}`, { allotted: true });
                              toast.success(`${r.payer_name} marked allotted`);
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-white border border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`mark-allotted-${r.receipt_id}`}
                        ><CheckCircle className="w-3 h-3" /> Mark allotted</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {committees.length === 0 ? (
        <div className="dashboard-card p-12 text-center text-slate-400">No committees yet. Add committees first from the Committees tab.</div>
      ) : (
        <>
          <div className="grid lg:grid-cols-3 gap-5">
          <form onSubmit={submit} className="dashboard-card p-5 space-y-3 lg:col-span-1 h-fit">
            <h4 className="font-bold text-sm">{editing ? "Edit allotment" : "New allotment"}</h4>
            {linkingReceipt && !editing && (() => {
              const r = cashPending.find((x) => x.receipt_id === linkingReceipt);
              return r ? (
                <div className="text-[11px] font-bold bg-orange-100 text-orange-800 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="allot-linked-receipt">
                  <span>Linked to receipt <span className="font-mono">{r.receipt_number}</span> — will disappear from cash list on submit.</span>
                  <button type="button" onClick={() => setLinkingReceipt(null)} className="text-orange-600 hover:text-orange-900 ml-2">×</button>
                </div>
              ) : null;
            })()}
            <select className="input-pill" value={form.committee_id} onChange={(e) => { setForm({ ...form, committee_id: e.target.value, portfolio: "" }); setSelCom(e.target.value); }} data-testid="allot-committee">
              {committees.map((c) => <option key={c.committee_id} value={c.committee_id}>{c.name}</option>)}
            </select>
            <input required placeholder="Student name" className="input-pill" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="allot-name" />
            <input required type="email" placeholder="Email" className="input-pill" value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} data-testid="allot-email" />
            <input placeholder="Phone" className="input-pill" value={form.student_phone} onChange={(e) => setForm({ ...form, student_phone: e.target.value })} />
            <input placeholder="School / Institute" className="input-pill" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} />
            <select required className="input-pill" value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} data-testid="allot-portfolio">
              <option value="">Select portfolio…</option>
              {availablePortfolios.map((p) => <option key={p} value={p}>{p}</option>)}
              {lockedPortfolios.map((p) => <option key={p} value={p} disabled>{p} (locked)</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} data-testid="allot-paid" /> Paid</label>
          <button type="submit" disabled={saving} className="btn-primary w-full py-3 disabled:opacity-60" data-testid="allot-submit">{saving ? "Saving…" : editing ? "Update" : "Allot & email PDF"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm({ committee_id: form.committee_id, student_name: "", student_email: "", student_phone: "", portfolio: "", school_name: "", paid: false }); }} className="btn-ghost w-full py-2 text-sm">Cancel edit</button>}
          </form>

          <div className="lg:col-span-2 dashboard-card p-5">
            <div className="flex items-center gap-2 mb-1 overflow-x-auto custom-scrollbar">
              {committees.map((c) => (
                <button key={c.committee_id} onClick={() => setSelCom(c.committee_id)} className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${selCom === c.committee_id ? "brand-gradient text-white" : "bg-slate-100 text-slate-600"}`}>
                  {c.name} <span className="opacity-70">({allotments.filter((a) => a.committee_id === c.committee_id).length})</span>
                </button>
              ))}
            </div>
            {/* Paid/unpaid breakdown + filter — reconciles with the matrix
                link, which counts ALL allotted portfolios (paid + unpaid). */}
            <div className="flex items-center gap-2 mb-3 text-xs" data-testid="paid-filter-row">
              <span className="text-slate-500" data-testid="com-allot-summary">
                {comAllAllotments.length} allotted · <span className="text-emerald-700 font-bold">{comPaid} paid</span> · <span className="text-amber-700 font-bold">{comAllAllotments.length - comPaid} unpaid</span>
              </span>
              <span className="text-slate-300">|</span>
              {[["all", "All"], ["paid", "Paid"], ["unpaid", "Unpaid only"]].map(([k, label]) => (
                <button key={k} onClick={() => setPaidFilter(k)} data-testid={`paid-filter-${k}`}
                  className={`px-2.5 py-0.5 rounded-full font-bold border ${paidFilter === k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                  {label}
                </button>
              ))}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-2">Portfolio</th><th className="py-2 pr-2">Delegate</th><th className="py-2 pr-2">School</th><th className="py-2 pr-2">Paid</th><th className="py-2 pr-2">Check-in</th><th></th>
              </tr></thead>
              <tbody>
                {comAllotments.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No allotments yet.</td></tr>}
                {comAllotments.length > 0 && comCheckedIn.length > 0 && (
                  <tr className="bg-emerald-50/40">
                    <td colSpan={6} className="py-2 px-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                      ✓ Checked in <span className="ml-1 bg-emerald-100 rounded-full px-1.5 py-0.5">{comCheckedIn.length}</span>
                    </td>
                  </tr>
                )}
                {comCheckedIn.map(renderRow)}
                {comAllotments.length > 0 && comNotCheckedIn.length > 0 && (
                  <tr className="bg-slate-50">
                    <td colSpan={6} className="py-2 px-2 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      ⏳ Not checked in <span className="ml-1 bg-slate-200 rounded-full px-1.5 py-0.5">{comNotCheckedIn.length}</span>
                      <span className="ml-2 font-normal text-slate-400 normal-case">— registered but didn&apos;t turn up (paid or unpaid)</span>
                    </td>
                  </tr>
                )}
                {comNotCheckedIn.map(renderRow)}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
      {dupModal && <DuplicateAllotmentsModal event={event} onClose={() => setDupModal(false)} onChanged={load} />}
      {cashDupModal && <DuplicateCashReceiptsModal event={event} onClose={() => setCashDupModal(false)} onChanged={load} />}
      {schoolPdfModal && <SchoolLettersModal event={event} allotments={allotments} onClose={() => setSchoolPdfModal(false)} />}
      {smartModal && <SmartReconcileModal event={event} onClose={() => setSmartModal(false)} onChanged={load} />}
    </div>
  );
}

const NO_SCHOOL_KEY = "__none__";

// Pick one or more schools → download their allotment letters (one student per page).
function SchoolLettersModal({ event, allotments, onClose }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set());
  const schools = useMemo(() => {
    const counts = {};
    allotments.forEach((a) => { const k = (a.school_name || "").trim() || NO_SCHOOL_KEY; counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).map(([key, count]) => ({ key, label: key === NO_SCHOOL_KEY ? "No school" : key, count }))
      .sort((x, y) => x.label.localeCompare(y.label));
  }, [allotments]);
  const visible = schools.filter((s) => s.label.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (k) => setPicked((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allVisiblePicked = visible.length > 0 && visible.every((s) => picked.has(s.key));
  const toggleAllVisible = () => setPicked((p) => { const n = new Set(p); visible.forEach((s) => (allVisiblePicked ? n.delete(s.key) : n.add(s.key))); return n; });
  const pages = schools.filter((s) => picked.has(s.key)).reduce((t, s) => t + s.count, 0);

  const download = () => {
    const token = localStorage.getItem("munners_token");
    const qs = [...picked].map((k) => `schools=${encodeURIComponent(k)}`).join("&");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/allotments/school-letters?${qs}&t=${token}`, "_blank");
    toast.success(picked.size === 1 ? `Preparing PDF (${pages} pages)…` : `Preparing ZIP of ${picked.size} school PDFs…`);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col p-6" onClick={(e) => e.stopPropagation()} data-testid="school-letters-modal">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-orange-500" /> Allotment letters by school</h4>
          <button onClick={onClose} className="btn-ghost text-xs py-1" data-testid="school-letters-close">Close</button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Same letter that's emailed, one student per page, sorted by name. One school downloads a single PDF; several download a ZIP with one PDF per school.</p>
        <input className="input-pill mb-2" placeholder="Search school name…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="school-letters-search" />
        <button onClick={toggleAllVisible} className="text-xs font-semibold text-orange-600 self-start mb-2" data-testid="school-letters-select-all">{allVisiblePicked ? "Clear shown" : "Select all shown"}</button>
        <div className="flex-1 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
          {visible.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No schools match.</div>}
          {visible.map((s) => (
            <label key={s.key} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`school-letters-option-${s.label.replace(/\W+/g, "-").toLowerCase()}`}>
              <input type="checkbox" checked={picked.has(s.key)} onChange={() => toggle(s.key)} className="w-4 h-4 accent-orange-500" />
              <span className="flex-1 text-sm truncate">{s.label}</span>
              <span className="text-xs text-slate-500">{s.count} student{s.count === 1 ? "" : "s"}</span>
            </label>
          ))}
        </div>
        <button onClick={download} disabled={picked.size === 0} className="btn-primary mt-4 justify-center disabled:opacity-50" data-testid="school-letters-download-btn">
          <Download className="w-4 h-4" /> {picked.size === 0 ? "Select a school" : picked.size === 1 ? `Download PDF (${pages} pages)` : `Download ZIP (${picked.size} schools, ${pages} pages)`}
        </button>
      </div>
    </div>
  );
}

// =========================================================
// FIND DUPLICATES — allotments sharing committee+portfolio,
// the same student name, or the same email. Delete extras inline.
// =========================================================
function DuplicateAllotmentsModal({ event, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/events/${event.event_id}/allotments/duplicates`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); setData({ groups: [] }); }
  };
  useEffect(() => { load(); }, []);

  const del = async (a) => {
    if (!window.confirm(`Delete allotment for ${a.student_name} (${a.committee} · ${a.portfolio})?\n\nThis cannot be undone.`)) return;
    setBusy(a.allotment_id);
    try {
      await api.delete(`/allotments/${a.allotment_id}`);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="duplicate-allotments-modal">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display font-bold text-lg flex items-center gap-2"><CopyX className="w-5 h-5 text-rose-500" /> Duplicate allotments</h4>
          <button onClick={onClose} className="btn-ghost text-xs py-1" data-testid="dup-allot-close">Close</button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Groups below share a committee+portfolio, student name, or email. The OLDEST entry is marked "keep" — review and delete the extras.</p>
        {!data ? (
          <div className="text-center py-8 text-slate-400 text-sm">Scanning…</div>
        ) : data.groups.length === 0 ? (
          <div className="text-center py-8 text-emerald-600 font-semibold text-sm" data-testid="dup-allot-none">✓ No duplicates found across {data.total_allotments} allotments.</div>
        ) : (
          <div className="space-y-4">
            {data.groups.map((g, gi) => (
              <div key={gi} className="rounded-xl border border-rose-100 bg-rose-50/30 p-3" data-testid={`dup-allot-group-${gi}`}>
                <div className="text-xs font-bold text-rose-700 mb-2">{g.label}: <span className="font-mono">{g.key || "(blank)"}</span></div>
                <div className="space-y-1.5">
                  {g.allotments.map((a, ai) => (
                    <div key={a.allotment_id} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2 border border-slate-100" data-testid={`dup-allot-row-${a.allotment_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{a.student_name} <span className="text-xs text-slate-400 font-normal">{a.student_email}</span></div>
                        <div className="text-xs text-slate-500 truncate">{a.committee} · {a.portfolio} · {a.paid ? "Paid" : "Unpaid"}{a.checked_in ? " · checked in" : ""}</div>
                      </div>
                      {ai === 0
                        ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 shrink-0">KEEP (oldest)</span>
                        : <button onClick={() => del(a)} disabled={busy === a.allotment_id} className="text-[10px] font-bold text-white bg-rose-500 hover:bg-rose-600 rounded-full px-2.5 py-1 shrink-0 disabled:opacity-50" data-testid={`dup-allot-del-${a.allotment_id}`}>Delete</button>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================
// FIND DUPLICATES — cash receipts sharing a receipt number or
// payer + amount. Deletes go through the accountant endpoint
// (which also reverses the journal entries).
// =========================================================
function DuplicateCashReceiptsModal({ event, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/accountant/receipts/cash-duplicates?event_id=${event.event_id}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); setData({ groups: [] }); }
  };
  useEffect(() => { load(); }, []);

  const del = async (r) => {
    if (!window.confirm(`Delete receipt ${r.receipt_number} for ${r.payer_name} (₹${(r.amount_paise / 100).toLocaleString("en-IN")})?\n\nJournal entries will be reversed. This cannot be undone.`)) return;
    setBusy(r.receipt_id);
    try {
      await api.delete(`/accountant/receipts/${r.receipt_id}`);
      toast.success(`Receipt ${r.receipt_number} deleted`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed (accountant/admin only)"); }
    finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="duplicate-cash-modal">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display font-bold text-lg flex items-center gap-2"><CopyX className="w-5 h-5 text-rose-500" /> Duplicate receipts (this event)</h4>
          <button onClick={onClose} className="btn-ghost text-xs py-1" data-testid="dup-cash-close">Close</button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Scans <b>all</b> receipts for this event — cash AND online, allotted or not. Groups share a receipt number, or the same payer + amount. The OLDEST receipt is marked "keep" — delete extras to stop them double-counting.</p>
        {!data ? (
          <div className="text-center py-8 text-slate-400 text-sm">Scanning…</div>
        ) : data.groups.length === 0 ? (
          <div className="text-center py-8 text-emerald-600 font-semibold text-sm" data-testid="dup-cash-none">✓ No duplicates found across {data.total_cash_receipts} receipts.</div>
        ) : (
          <div className="space-y-4">
            {data.groups.map((g, gi) => (
              <div key={gi} className="rounded-xl border border-rose-100 bg-rose-50/30 p-3" data-testid={`dup-cash-group-${gi}`}>
                <div className="text-xs font-bold text-rose-700 mb-2">{g.label}: <span className="font-mono">{g.key || "(blank)"}</span></div>
                <div className="space-y-1.5">
                  {g.receipts.map((r, ri) => (
                    <div key={r.receipt_id} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2 border border-slate-100" data-testid={`dup-cash-row-${r.receipt_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{r.payer_name} <span className="font-mono text-xs text-slate-400">{r.receipt_number}</span></div>
                        <div className="text-xs text-slate-500 truncate">₹{(r.amount_paise / 100).toLocaleString("en-IN")} · {r.payment_mode === "cash" ? "Cash" : "Online"} · {(r.issued_at || "").slice(0, 16).replace("T", " ")}{r.school_name ? ` · ${r.school_name}` : ""}{r.allotted ? " · allotted" : ""}</div>
                      </div>
                      {ri === 0
                        ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 shrink-0">KEEP (oldest)</span>
                        : <button onClick={() => del(r)} disabled={busy === r.receipt_id} className="text-[10px] font-bold text-white bg-rose-500 hover:bg-rose-600 rounded-full px-2.5 py-1 shrink-0 disabled:opacity-50" data-testid={`dup-cash-del-${r.receipt_id}`}>Delete</button>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
